import {
  useV2CreateQuestionnaireSectionMutation,
  useV2UpdateQuestionnaireSectionMutation,
  useV2DeleteQuestionnaireSectionMutation,
} from "@generated";
import type { V2QuestionnaireSectionResource } from "@generated";
import type { SectionFormValues } from "../types";

// NOTE: questionnaire-section ids are UUID strings (not integers), so they must
// NOT be coerced with Number(). The generated SectionApiArg.id is now typed
// `string`, matching the resource id, so no cast is needed.
export const buildSectionData = (
  values: SectionFormValues,
  id?: string
): V2QuestionnaireSectionResource =>
  ({
    type: "questionnaire-sections",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      expression: values.expression,
      introduction: values.introduction,
    },
  } as unknown as V2QuestionnaireSectionResource);

export const useSectionMutations = (questionnaireId: string) => {
  const [create] = useV2CreateQuestionnaireSectionMutation();
  const [update] = useV2UpdateQuestionnaireSectionMutation();
  const [destroy] = useV2DeleteQuestionnaireSectionMutation();
  const qid = Number(questionnaireId);

  return {
    create: (values: SectionFormValues) =>
      create({
        questionnaireId: qid,
        body: { data: buildSectionData(values) },
      }).unwrap(),
    update: (id: string, values: SectionFormValues) =>
      update({
        questionnaireId: qid,
        id,
        body: { data: buildSectionData(values, id) },
      }).unwrap(),
    remove: (id: string) =>
      destroy({
        questionnaireId: qid,
        id,
      }).unwrap(),
    reorder: (id: string, position: number) =>
      update({
        questionnaireId: qid,
        id,
        body: {
          data: {
            type: "questionnaire-sections",
            id,
            attributes: { position },
          } as unknown as V2QuestionnaireSectionResource,
        },
      }).unwrap(),
  };
};

export default useSectionMutations;
